import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Brain, Send, FileText, Users, BarChart3, Settings, 
  Trash2, Download, Eye, CheckCircle, Clock, AlertCircle,
  PlusCircle, X, FileDown, FileSpreadsheet, Edit,
  Upload, Loader2, AlertTriangle, RefreshCw, FileUser
} from 'lucide-react';
import SendModal from '../components/SendModal';

// Extracted assessment components
import { 
  AssessmentCard, 
  AssessmentStatsCards, 
  AssessmentEmptyState, 
  AssessmentConfigAlert,
  isAssessmentExpired 
} from '../components/assessments';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Assessments = () => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [participationOpen, setParticipationOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [results, setResults] = useState(null);
  const [participation, setParticipation] = useState(null);
  const [config, setConfig] = useState(null);
  
  // Import modal state
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form state for creating assessment
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    data_inicio: '',
    data_fim: '',
    perguntas: [{ pergunta: '', tipo: 'escala_1_4', opcoes: [], obrigatoria: true, categoria: '' }]
  });
  
  // Send modal state
  const [sendOpen, setSendOpen] = useState(false);
  const [sendAssessmentId, setSendAssessmentId] = useState(null);
  const [selectedAssessmentForSend, setSelectedAssessmentForSend] = useState(null);
  
  // Edit modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  
  // Sync counters state
  const [syncing, setSyncing] = useState(false);
  
  // Totals from database (real-time aggregated)
  const [dbTotals, setDbTotals] = useState({ enviados: 0, respondidos: 0 });
  
  // COPSOQ II Template state
  const [useCopsoqTemplate, setUseCopsoqTemplate] = useState(false);
  const [copsoqResults, setCopsoqResults] = useState(null);
  const [copsoqResultsOpen, setCopsoqResultsOpen] = useState(false);
  const [loadingCopsoqResults, setLoadingCopsoqResults] = useState(false);
  
  // PGR Integration state
  const [syncingToPgr, setSyncingToPgr] = useState(false);
  const [selectedCopsoqAssessmentId, setSelectedCopsoqAssessmentId] = useState(null);
  
  // Individual Report state
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(null); // employee_id being generated

  // COPSOQ II Template - Versão Média Portugal 2013
  const COPSOQ_II_TEMPLATE = {
    titulo: "Avaliação Psicossocial - COPSOQ II",
    descricao: "Questionário de avaliação de fatores psicossociais no trabalho baseado no Copenhagen Psychosocial Questionnaire (COPSOQ II) - Versão Média Portugal 2013. Este questionário é anônimo e confidencial.",
    tipo: "copsoq_ii",
    dimensoes: [
      {
        id: "exigencias_laborais",
        nome: "Exigências Laborais",
        descricao: "Avalia as exigências quantitativas, ritmo de trabalho, exigências cognitivas e emocionais",
        cor: "#ef4444",
        perguntas: [
          { pergunta: "A sua carga de trabalho acumula-se por ser mal distribuída?", categoria: "Exigências Quantitativas", invertida: false },
          { pergunta: "Com que frequência não tem tempo para completar todas as tarefas do seu trabalho?", categoria: "Exigências Quantitativas", invertida: false },
          { pergunta: "Precisa de trabalhar muito rapidamente?", categoria: "Ritmo de Trabalho", invertida: false },
          { pergunta: "Trabalha a um ritmo elevado durante todo o dia?", categoria: "Ritmo de Trabalho", invertida: false },
          { pergunta: "O seu trabalho exige a sua atenção constante?", categoria: "Exigências Cognitivas", invertida: false },
          { pergunta: "O seu trabalho requer que tome decisões difíceis?", categoria: "Exigências Cognitivas", invertida: false },
          { pergunta: "O seu trabalho exige que lide com os problemas de outras pessoas?", categoria: "Exigências Emocionais", invertida: false },
          { pergunta: "O seu trabalho é emocionalmente exigente?", categoria: "Exigências Emocionais", invertida: false },
          { pergunta: "O seu trabalho exige que esconda os seus sentimentos?", categoria: "Exigências para Esconder Emoções", invertida: false }
        ]
      },
      {
        id: "organizacao_conteudo",
        nome: "Organização e Conteúdo do Trabalho",
        descricao: "Avalia influência no trabalho, possibilidades de desenvolvimento, significado e compromisso",
        cor: "#3b82f6",
        perguntas: [
          { pergunta: "Tem um elevado grau de influência no seu trabalho?", categoria: "Influência no Trabalho", invertida: true },
          { pergunta: "Participa na escolha das pessoas com quem trabalha?", categoria: "Influência no Trabalho", invertida: true },
          { pergunta: "Pode influenciar a quantidade de trabalho que lhe compete a si?", categoria: "Influência no Trabalho", invertida: true },
          { pergunta: "O seu trabalho exige que tenha iniciativa?", categoria: "Possibilidades de Desenvolvimento", invertida: true },
          { pergunta: "O seu trabalho permite-lhe aprender coisas novas?", categoria: "Possibilidades de Desenvolvimento", invertida: true },
          { pergunta: "O seu trabalho permite-lhe usar as suas habilidades ou competências?", categoria: "Possibilidades de Desenvolvimento", invertida: true },
          { pergunta: "O seu trabalho é variado?", categoria: "Variedade do Trabalho", invertida: true },
          { pergunta: "As suas tarefas têm algum significado para si?", categoria: "Significado do Trabalho", invertida: true },
          { pergunta: "Sente que o seu trabalho é importante?", categoria: "Significado do Trabalho", invertida: true },
          { pergunta: "Sente-se motivado(a) e envolvido(a) com o seu trabalho?", categoria: "Compromisso com o Local de Trabalho", invertida: true },
          { pergunta: "Gosta de falar sobre o seu local de trabalho com outras pessoas?", categoria: "Compromisso com o Local de Trabalho", invertida: true }
        ]
      },
      {
        id: "relacoes_sociais",
        nome: "Relações Sociais e Liderança",
        descricao: "Avalia previsibilidade, reconhecimento, apoio social, qualidade da liderança e confiança",
        cor: "#8b5cf6",
        perguntas: [
          { pergunta: "No seu local de trabalho, é informado(a) com antecedência sobre decisões importantes?", categoria: "Previsibilidade", invertida: true },
          { pergunta: "Recebe toda a informação de que necessita para fazer bem o seu trabalho?", categoria: "Previsibilidade", invertida: true },
          { pergunta: "O seu trabalho é reconhecido e apreciado pela gestão?", categoria: "Reconhecimento", invertida: true },
          { pergunta: "É tratado(a) de forma justa no seu local de trabalho?", categoria: "Reconhecimento", invertida: true },
          { pergunta: "A gestão do seu local de trabalho respeita-o(a)?", categoria: "Reconhecimento", invertida: true },
          { pergunta: "Com que frequência tem ajuda e apoio dos seus colegas de trabalho?", categoria: "Apoio Social Colegas", invertida: true },
          { pergunta: "Com que frequência os seus colegas estão dispostos a ouvi-lo(a)?", categoria: "Apoio Social Colegas", invertida: true },
          { pergunta: "Com que frequência o seu superior imediato está disposto a ouvi-lo(a)?", categoria: "Apoio Social Superiores", invertida: true },
          { pergunta: "Com que frequência tem ajuda e apoio do seu superior imediato?", categoria: "Apoio Social Superiores", invertida: true },
          { pergunta: "O seu superior imediato é bom a planear o trabalho?", categoria: "Qualidade da Liderança", invertida: true },
          { pergunta: "O seu superior imediato é bom a resolver conflitos?", categoria: "Qualidade da Liderança", invertida: true },
          { pergunta: "Os funcionários ocultam informações uns dos outros?", categoria: "Confiança Horizontal", invertida: false },
          { pergunta: "Os funcionários confiam uns nos outros de um modo geral?", categoria: "Confiança Horizontal", invertida: true },
          { pergunta: "A gestão confia nos seus funcionários para fazerem o seu trabalho bem?", categoria: "Confiança Vertical", invertida: true },
          { pergunta: "Confia na informação que lhe é transmitida pela gestão?", categoria: "Confiança Vertical", invertida: true },
          { pergunta: "A gestão oculta informação aos seus funcionários?", categoria: "Confiança Vertical", invertida: false }
        ]
      },
      {
        id: "interface_trabalho",
        nome: "Interface Trabalho-Indivíduo",
        descricao: "Avalia satisfação no trabalho, conflito trabalho-família e insegurança laboral",
        cor: "#f59e0b",
        perguntas: [
          { pergunta: "Em relação ao seu trabalho em geral, quão satisfeito(a) está com as suas perspectivas?", categoria: "Satisfação no Trabalho", invertida: true },
          { pergunta: "Em relação ao seu trabalho em geral, quão satisfeito(a) está com as condições físicas?", categoria: "Satisfação no Trabalho", invertida: true },
          { pergunta: "Em relação ao seu trabalho em geral, quão satisfeito(a) está com a forma como as suas capacidades são utilizadas?", categoria: "Satisfação no Trabalho", invertida: true },
          { pergunta: "Em relação ao seu trabalho em geral, quão satisfeito(a) está com o seu trabalho de uma forma global?", categoria: "Satisfação no Trabalho", invertida: true },
          { pergunta: "Sente que o seu trabalho lhe exige muita energia que acaba por afetar a sua vida privada negativamente?", categoria: "Conflito Trabalho-Família", invertida: false },
          { pergunta: "Sente que o seu trabalho lhe exige muito tempo que acaba por afetar a sua vida privada negativamente?", categoria: "Conflito Trabalho-Família", invertida: false },
          { pergunta: "A sua família e os seus amigos dizem-lhe que trabalha demais?", categoria: "Conflito Trabalho-Família", invertida: false },
          { pergunta: "Sente-se preocupado(a) em ficar desempregado(a)?", categoria: "Insegurança Laboral", invertida: false },
          { pergunta: "Sente-se preocupado(a) com ser transferido(a) para outro local de trabalho contra a sua vontade?", categoria: "Insegurança Laboral", invertida: false },
          { pergunta: "Sente-se preocupado(a) em ser difícil encontrar outro emprego se ficar desempregado(a)?", categoria: "Insegurança Laboral", invertida: false }
        ]
      },
      {
        id: "saude_bemestar",
        nome: "Saúde e Bem-Estar",
        descricao: "Avalia saúde geral, problemas em dormir, burnout, stress e sintomas depressivos",
        cor: "#10b981",
        perguntas: [
          { pergunta: "Em geral, sente que a sua saúde é...", categoria: "Saúde Geral", invertida: true, escala_diferente: "saude" },
          { pergunta: "Com que frequência teve problemas em dormir?", categoria: "Problemas em Dormir", invertida: false },
          { pergunta: "Com que frequência dormiu mal e de forma inquieta?", categoria: "Problemas em Dormir", invertida: false },
          { pergunta: "Com que frequência se sentiu fisicamente exausto(a)?", categoria: "Burnout", invertida: false },
          { pergunta: "Com que frequência se sentiu emocionalmente exausto(a)?", categoria: "Burnout", invertida: false },
          { pergunta: "Com que frequência se sentiu esgotado(a)?", categoria: "Burnout", invertida: false },
          { pergunta: "Com que frequência se sentiu stressado(a)?", categoria: "Stress", invertida: false },
          { pergunta: "Com que frequência se sentiu irritado(a)?", categoria: "Stress", invertida: false },
          { pergunta: "Com que frequência se sentiu ansioso(a)?", categoria: "Stress", invertida: false },
          { pergunta: "Com que frequência se sentiu triste?", categoria: "Sintomas Depressivos", invertida: false },
          { pergunta: "Com que frequência sentiu falta de interesse pelas coisas do dia-a-dia?", categoria: "Sintomas Depressivos", invertida: false },
          { pergunta: "Com que frequência sentiu falta de energia e força?", categoria: "Sintomas Depressivos", invertida: false }
        ]
      }
    ],
    escala: {
      tipo: "likert_5",
      opcoes: [
        { valor: 1, texto: "Nunca/Quase nunca" },
        { valor: 2, texto: "Raramente" },
        { valor: 3, texto: "Às vezes" },
        { valor: 4, texto: "Frequentemente" },
        { valor: 5, texto: "Sempre" }
      ],
      escala_saude: [
        { valor: 1, texto: "Excelente" },
        { valor: 2, texto: "Muito boa" },
        { valor: 3, texto: "Boa" },
        { valor: 4, texto: "Razoável" },
        { valor: 5, texto: "Deficiente" }
      ]
    },
    interpretacao: {
      verde: { min: 3.67, max: 5, label: "Favorável para a Saúde", cor: "#10b981" },
      amarelo: { min: 2.34, max: 3.66, label: "Situação Intermédia", cor: "#f59e0b" },
      vermelho: { min: 1, max: 2.33, label: "Risco para a Saúde", cor: "#ef4444" }
    }
  };

  // Function to load COPSOQ II template
  const loadCopsoqTemplate = () => {
    const allQuestions = [];
    COPSOQ_II_TEMPLATE.dimensoes.forEach(dimensao => {
      dimensao.perguntas.forEach(p => {
        allQuestions.push({
          pergunta: p.pergunta,
          tipo: 'escala_1_5',
          opcoes: [],
          obrigatoria: true,
          categoria: `${dimensao.nome} - ${p.categoria}`,
          dimensao_id: dimensao.id,
          dimensao_nome: dimensao.nome,
          invertida: p.invertida || false,
          escala_diferente: p.escala_diferente || null
        });
      });
    });
    
    setFormData({
      titulo: COPSOQ_II_TEMPLATE.titulo,
      descricao: COPSOQ_II_TEMPLATE.descricao,
      data_inicio: '',
      data_fim: '',
      perguntas: allQuestions,
      tipo: 'copsoq_ii',
      copsoq_config: {
        dimensoes: COPSOQ_II_TEMPLATE.dimensoes.map(d => ({
          id: d.id,
          nome: d.nome,
          descricao: d.descricao,
          cor: d.cor
        })),
        escala: COPSOQ_II_TEMPLATE.escala,
        interpretacao: COPSOQ_II_TEMPLATE.interpretacao
      }
    });
    setUseCopsoqTemplate(true);
    toast.success('Template COPSOQ II carregado com sucesso!');
  };

  // Reset to custom assessment
  const resetToCustomAssessment = () => {
    setFormData({
      titulo: '',
      descricao: '',
      data_inicio: '',
      data_fim: '',
      perguntas: [{ pergunta: '', tipo: 'escala_1_4', opcoes: [], obrigatoria: true, categoria: '' }]
    });
    setUseCopsoqTemplate(false);
  };

  useEffect(() => {
    fetchData();
    fetchConfig();
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      setLoading(true);
      fetchData();
      fetchConfig();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, []);

  const fetchData = async () => {
    try {
      // Fetch assessments and totals in parallel
      const [assessmentsRes, totalsRes] = await Promise.all([
        axios.get(`${API_URL}/custom-assessments`),
        axios.get(`${API_URL}/assessment-totals`).catch(() => ({ data: null }))
      ]);
      
      setAssessments(assessmentsRes.data);
      
      // Use real totals from database if available
      if (totalsRes.data) {
        setDbTotals({
          enviados: totalsRes.data.geral.enviados,
          respondidos: totalsRes.data.geral.respondidos
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/assessment-config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  // Fetch COPSOQ II Results
  const fetchCopsoqResults = async (assessmentId) => {
    setLoadingCopsoqResults(true);
    setSelectedCopsoqAssessmentId(assessmentId);
    try {
      const response = await axios.get(`${API_URL}/custom-assessments/${assessmentId}/copsoq-results`);
      setCopsoqResults(response.data);
      setCopsoqResultsOpen(true);
    } catch (error) {
      console.error('Error fetching COPSOQ results:', error);
      toast.error(error.response?.data?.detail || 'Erro ao carregar resultados COPSOQ II');
    } finally {
      setLoadingCopsoqResults(false);
    }
  };

  // Sync COPSOQ to PGR
  const handleSyncToPgr = async (assessmentId) => {
    setSyncingToPgr(true);
    try {
      const response = await axios.post(`${API_URL}/psychosocial/sync-to-pgr/${assessmentId}`);
      const { stats } = response.data;
      toast.success(
        `Sincronização concluída! Riscos: ${stats.risks_created} criados, ${stats.risks_updated} atualizados. ` +
        `Planos de ação: ${stats.action_plans_created} criados.`
      );
    } catch (error) {
      console.error('Error syncing to PGR:', error);
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar com PGR');
    } finally {
      setSyncingToPgr(false);
    }
  };

  // Fetch participants for individual reports
  const fetchParticipants = async (assessmentId) => {
    setLoadingParticipants(true);
    setSelectedCopsoqAssessmentId(assessmentId);
    try {
      const response = await axios.get(`${API_URL}/psychosocial/assessment/${assessmentId}/participants`);
      setParticipants(response.data.participants || []);
      setParticipantsOpen(true);
    } catch (error) {
      console.error('Error fetching participants:', error);
      toast.error(error.response?.data?.detail || 'Erro ao carregar participantes');
    } finally {
      setLoadingParticipants(false);
    }
  };

  // Generate individual PDF report
  const handleGenerateIndividualReport = async (assessmentId, employeeId, employeeName) => {
    setGeneratingReport(employeeId);
    try {
      const response = await axios.get(
        `${API_URL}/psychosocial/individual-report/${assessmentId}/${employeeId}/pdf`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `laudo_individual_${employeeName.replace(/\s+/g, '_').toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Laudo de ${employeeName} gerado com sucesso!`);
    } catch (error) {
      console.error('Error generating individual report:', error);
      if (error.response?.status === 404) {
        toast.error('Funcionário não respondeu a avaliação ou não foi encontrado');
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao gerar laudo individual');
      }
    } finally {
      setGeneratingReport(null);
    }
  };

  // Get semaphore color
  const getSemaforoColor = (semaforo) => {
    switch(semaforo) {
      case 'verde': return '#10b981';
      case 'amarelo': return '#f59e0b';
      case 'vermelho': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Sync assessment counters (admin only)
  const handleSyncCounters = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/admin/sync-assessment-counters`);
      
      // Update totals from sync response
      if (response.data.total_enviados !== undefined) {
        setDbTotals({
          enviados: response.data.total_enviados,
          respondidos: response.data.total_respondidos
        });
      }
      
      toast.success(`${response.data.message} - Enviados: ${response.data.total_enviados}, Respostas: ${response.data.total_respondidos}`);
      // Refresh data to show updated counters
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Apenas administradores podem executar esta ação');
      } else {
        toast.error('Erro ao sincronizar contadores');
      }
    } finally {
      setSyncing(false);
    }
  };

  // Download assessment template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/custom-assessments/template`);
      const { filename, content } = response.data;
      
      // Decode base64 and download
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  // Import assessment from Excel
  const handleImportAssessment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Arquivo deve ser Excel (.xlsx ou .xls)');
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/custom-assessments/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { titulo, total_perguntas, errors, message } = response.data;
      
      if (errors && errors.length > 0) {
        toast.warning(message);
        console.log('Import errors:', errors);
      } else {
        toast.success(`Avaliação "${titulo}" importada com ${total_perguntas} perguntas!`);
      }
      
      setImportOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar avaliação');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCreateAssessment = async (e) => {
    e.preventDefault();
    try {
      // Salvar no formato compatível com o banco existente
      const payload = {
        titulo: formData.titulo,
        descricao: formData.descricao,
        perguntas: formData.perguntas.map(p => ({
          id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
          pergunta: p.pergunta,
          tipo: p.tipo || 'escala_1_4',
          opcoes: p.opcoes || null,
          obrigatoria: p.obrigatoria !== false,
          categoria: p.categoria || ''
        })),
        status: 'rascunho',
        data_inicio: formData.data_inicio || new Date().toISOString().split('T')[0],
        data_fim: formData.data_fim || '',
        total_enviados: 0,
        total_respondidos: 0
      };
      
      await axios.post(`${API_URL}/custom-assessments/legacy`, payload);
      toast.success('Avaliação criada com sucesso!');
      setCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Erro ao criar avaliação:', error.response?.data);
      toast.error('Erro ao criar avaliação');
    }
  };

  const handleSendAssessment = async (assessmentId) => {
    // Encontrar a avaliação selecionada
    const assessment = assessments.find(a => a.id === assessmentId);
    setSendAssessmentId(assessmentId);
    setSelectedAssessmentForSend(assessment);
    setSendOpen(true);
  };
  
  const confirmSendAssessment = async (method, employeeIds, filterOptions) => {
    try {
      // Construir body baseado no método e funcionários selecionados
      let body = { 
        employee_ids: employeeIds,
        send_method: method 
      };
      
      const response = await axios.post(`${API_URL}/custom-assessments/${sendAssessmentId}/send`, body);
      
      let message = `Avaliação enviada para ${response.data.novos_envios || employeeIds.length} funcionário(s)!`;
      if (response.data.ja_convidados > 0) {
        message += ` (${response.data.ja_convidados} já haviam sido convidados)`;
      }
      toast.success(message);
      
      if (response.data.funcionarios_sem_whatsapp > 0 && method === 'whatsapp') {
        toast.warning(`${response.data.funcionarios_sem_whatsapp} funcionários sem WhatsApp cadastrado`);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar avaliação');
      throw error;
    }
  };
  
  const handleEditAssessment = (assessment) => {
    // Mapear campos do formato legado para o frontend
    // Legado usa: titulo, descricao, perguntas[{pergunta, tipo, categoria, obrigatoria, opcoes}]
    setEditData({
      id: assessment.id,
      titulo: assessment.titulo || assessment.title || '',
      descricao: assessment.descricao || assessment.description || '',
      perguntas: (assessment.perguntas || assessment.questions || []).map(p => ({
        id: p.id,
        texto: p.pergunta || p.text || '',
        tipo_resposta: p.tipo || p.type || 'escala_1_4',
        obrigatoria: p.obrigatoria !== false && p.required !== false,
        categoria: p.categoria || p.category || '',
        opcoes: p.opcoes || p.options || []
      }))
    });
    setEditOpen(true);
  };
  
  const handleSaveEdit = async () => {
    try {
      // Salvar no formato compatível com o banco existente
      const payload = {
        titulo: editData.titulo,
        descricao: editData.descricao,
        perguntas: editData.perguntas.map(p => ({
          id: p.id || (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9)),
          pergunta: p.texto,
          tipo: p.tipo_resposta || 'escala_1_4',
          opcoes: p.opcoes || null,
          obrigatoria: p.obrigatoria !== false,
          categoria: p.categoria || ''
        }))
      };
      
      await axios.put(`${API_URL}/custom-assessments/${editData.id}/legacy`, payload);
      toast.success('Avaliação atualizada com sucesso!');
      setEditOpen(false);
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar avaliação:', error.response?.data);
      toast.error('Erro ao atualizar avaliação');
    }
  };
  
  const updateEditQuestion = (index, field, value) => {
    const newPerguntas = [...editData.perguntas];
    newPerguntas[index] = { ...newPerguntas[index], [field]: value };
    setEditData({ ...editData, perguntas: newPerguntas });
  };
  
  const addEditQuestion = () => {
    setEditData({
      ...editData,
      perguntas: [...editData.perguntas, { texto: '', tipo_resposta: 'escala_1_4', obrigatoria: true, categoria: '', opcoes: [] }]
    });
  };
  
  const removeEditQuestion = (index) => {
    const newPerguntas = editData.perguntas.filter((_, i) => i !== index);
    setEditData({ ...editData, perguntas: newPerguntas });
  };

  const handleDownloadPDF = async (assessmentId, type = 'assessment') => {
    try {
      const endpoint = type === 'results' 
        ? `${API_URL}/custom-assessments/${assessmentId}/results/pdf`
        : `${API_URL}/custom-assessments/${assessmentId}/pdf`;
      
      // Use blob response type for PDF download with credentials
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        withCredentials: true
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = type === 'results' ? 'resultados_avaliacao.pdf' : 'avaliacao.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao gerar PDF. Verifique se há respostas na avaliação.');
    }
  };

  const handleViewResults = async (assessment) => {
    try {
      const response = await axios.get(`${API_URL}/custom-assessments/${assessment.id}/results`, {
        withCredentials: true
      });
      setResults(response.data);
      setSelectedAssessment(assessment);
      setResultsOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar resultados');
    }
  };

  const handleViewParticipation = async (assessment) => {
    try {
      const response = await axios.get(`${API_URL}/custom-assessments/${assessment.id}/participation`, {
        withCredentials: true
      });
      setParticipation(response.data);
      setSelectedAssessment(assessment);
      setParticipationOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar participação');
    }
  };

  const handleDeleteAssessment = async (assessmentId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta avaliação? Esta ação não pode ser desfeita.')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/custom-assessments/${assessmentId}`);
      toast.success('Avaliação excluída');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir avaliação');
    }
  };

  const downloadExcelFile = (content, filename) => {
    const byteCharacters = atob(content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportAssessments = async () => {
    try {
      const response = await axios.get(`${API_URL}/custom-assessments/export`);
      downloadExcelFile(response.data.content, response.data.filename);
      toast.success(`Planilha gerada com ${response.data.total} avaliação(ões)!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao exportar avaliações');
    }
  };

  const handleExportResponses = async (assessmentId, titulo) => {
    try {
      const response = await axios.get(`${API_URL}/custom-assessments/${assessmentId}/export-responses`);
      downloadExcelFile(response.data.content, response.data.filename);
      toast.success(`Planilha gerada com ${response.data.total_responses} resposta(s)!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao exportar respostas');
    }
  };

  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        `${API_URL}/assessment-config?frequencia=${config.frequencia}&dias_antecedencia=${config.dias_antecedencia_aviso}`
      );
      toast.success(`Configuração atualizada! Próxima avaliação: ${response.data.proxima_avaliacao}`);
      setConfigOpen(false);
      fetchConfig();
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    }
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      perguntas: [...formData.perguntas, { pergunta: '', tipo: 'escala_1_5', opcoes: [], obrigatoria: true, categoria: '' }]
    });
  };

  const removeQuestion = (index) => {
    const newPerguntas = formData.perguntas.filter((_, i) => i !== index);
    setFormData({ ...formData, perguntas: newPerguntas });
  };

  const updateQuestion = (index, field, value) => {
    const newPerguntas = [...formData.perguntas];
    newPerguntas[index] = { ...newPerguntas[index], [field]: value };
    setFormData({ ...formData, perguntas: newPerguntas });
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      descricao: '',
      data_inicio: '',
      data_fim: '',
      perguntas: [{ pergunta: '', tipo: 'escala_1_4', opcoes: [], obrigatoria: true, categoria: '' }]
    });
  };
  
  // Count active (not expired) assessments - uses imported isAssessmentExpired
  const getActiveCount = () => {
    return assessments.filter(a => {
      const status = a.status?.toLowerCase();
      if (status !== 'ativa' && status !== 'active') return false;
      return !isAssessmentExpired(a);
    }).length;
  };
  
  // Count expired assessments
  const getExpiredCount = () => {
    return assessments.filter(a => isAssessmentExpired(a)).length;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6" data-testid="assessments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            Avaliações Psicossociais
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Crie, envie e analise avaliações conforme NR-1
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleSyncCounters} 
            disabled={syncing}
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
            data-testid="sync-counters-btn"
            title="Sincroniza os contadores de respostas com os dados reais do banco"
          >
            {syncing ? (
              <Loader2 size={18} className="mr-2 animate-spin" />
            ) : (
              <BarChart3 size={18} className="mr-2" />
            )}
            {syncing ? 'Sincronizando...' : 'Sincronizar Contadores'}
          </Button>
          <Button variant="outline" onClick={handleExportAssessments} data-testid="export-assessments-btn">
            <FileSpreadsheet size={18} className="mr-2" />
            Exportar Avaliações
          </Button>
          <Button variant="outline" onClick={() => setConfigOpen(true)} data-testid="config-btn">
            <Settings size={18} className="mr-2" />
            Configurar Periodicidade
          </Button>
          
          {/* Import Modal */}
          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#0d9488] text-[#0d9488] hover:bg-[#e8f4fc]" data-testid="import-assessment-btn">
                <Upload size={18} className="mr-2" />
                Importar Excel
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-[#0d9488]" />
                  Importar Avaliação via Excel
                </DialogTitle>
                <DialogDescription>
                  Faça upload de um arquivo Excel com as perguntas da avaliação
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="p-4 bg-[#e8f4fc] rounded-lg space-y-3">
                  <p className="text-sm text-[#1e3a5f]">
                    <strong>Passo 1:</strong> Baixe o template Excel
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={handleDownloadTemplate}
                    className="w-full"
                  >
                    <Download size={18} className="mr-2" />
                    Baixar Template
                  </Button>
                </div>
                
                <div className="p-4 border rounded-lg space-y-3">
                  <p className="text-sm text-[#1e3a5f]">
                    <strong>Passo 2:</strong> Faça upload do arquivo preenchido
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleImportAssessment}
                      disabled={importing}
                      className="flex-1"
                    />
                  </div>
                  {importing && (
                    <div className="flex items-center gap-2 text-sm text-[#64748b]">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Importando...
                    </div>
                  )}
                </div>
                
                <div className="text-xs text-[#64748b] space-y-1">
                  <p><strong>Tipos de pergunta suportados:</strong></p>
                  <ul className="list-disc list-inside">
                    <li>escala - Escala de 1 a 4</li>
                    <li>texto - Resposta aberta</li>
                    <li>multipla_escolha - Seleção de opções</li>
                    <li>sim_nao - Sim ou Não</li>
                  </ul>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0d9488] hover:bg-[#0f766e]" data-testid="create-assessment-btn">
                <Plus size={18} className="mr-2" />
                Nova Avaliação
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Criar Nova Avaliação</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAssessment} className="space-y-6">
                {/* Template Selection */}
                <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Brain className="h-5 w-5 text-purple-600" />
                      <span className="font-semibold text-purple-900 dark:text-purple-100">Template COPSOQ II</span>
                      <Badge className="bg-purple-500 text-xs">Recomendado</Badge>
                    </div>
                    {!useCopsoqTemplate ? (
                      <Button 
                        type="button" 
                        onClick={loadCopsoqTemplate}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        size="sm"
                        data-testid="load-copsoq-btn"
                      >
                        Usar Template COPSOQ II
                      </Button>
                    ) : (
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={resetToCustomAssessment}
                        size="sm"
                        className="border-purple-300 text-purple-700"
                        data-testid="reset-template-btn"
                      >
                        <X size={14} className="mr-1" />
                        Criar Personalizada
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    {useCopsoqTemplate 
                      ? "✅ Template COPSOQ II carregado com 58 perguntas em 5 dimensões. O sistema calculará automaticamente os resultados por dimensão com interpretação semáforo."
                      : "Questionário Copenhagen de Riscos Psicossociais (Versão Média 2013) com 58 perguntas validadas cientificamente, organizadas em 5 dimensões com interpretação automática."}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Título da Avaliação *</Label>
                    <Input
                      value={formData.titulo}
                      onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                      placeholder="Ex: Avaliação Psicossocial Q1 2026"
                      required
                      disabled={useCopsoqTemplate}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Data Início</Label>
                      <Input
                        type="date"
                        value={formData.data_inicio}
                        onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Data Fim</Label>
                      <Input
                        type="date"
                        value={formData.data_fim}
                        onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label>Descrição</Label>
                  <Textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    placeholder="Instruções ou contexto para os funcionários..."
                    rows={2}
                    disabled={useCopsoqTemplate}
                  />
                </div>

                {/* COPSOQ II Dimensions Summary (when template is active) */}
                {useCopsoqTemplate && (
                  <div className="space-y-3">
                    <Label className="text-lg font-semibold flex items-center gap-2">
                      <BarChart3 size={20} className="text-purple-600" />
                      Dimensões COPSOQ II ({formData.perguntas.length} perguntas)
                    </Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {COPSOQ_II_TEMPLATE.dimensoes.map((dim) => (
                        <div 
                          key={dim.id} 
                          className="p-3 rounded-lg border"
                          style={{ borderLeftColor: dim.cor, borderLeftWidth: '4px' }}
                        >
                          <div className="font-medium text-sm" style={{ color: dim.cor }}>{dim.nome}</div>
                          <div className="text-xs text-slate-500 mt-1">{dim.perguntas.length} perguntas</div>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Escala Likert 1-5:</strong> Nunca/Quase nunca • Raramente • Às vezes • Frequentemente • Sempre
                      </p>
                    </div>
                  </div>
                )}

                {/* Custom Questions (when template is NOT active) */}
                {!useCopsoqTemplate && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-lg font-semibold">Perguntas</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                        <PlusCircle size={16} className="mr-1" />
                        Adicionar Pergunta
                      </Button>
                    </div>
                  
                  {formData.perguntas.map((pergunta, index) => (
                    <Card key={index} className="p-4 bg-slate-50 dark:bg-slate-900">
                      <div className="flex items-start gap-4">
                        <span className="bg-emerald-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                        <div className="flex-1 space-y-3">
                          <Input
                            value={pergunta.pergunta}
                            onChange={(e) => updateQuestion(index, 'pergunta', e.target.value)}
                            placeholder="Digite a pergunta..."
                            required
                          />
                          <Input
                            value={pergunta.categoria || ''}
                            onChange={(e) => updateQuestion(index, 'categoria', e.target.value)}
                            placeholder="Categoria/Identificação (ex: Clima Organizacional, Carga de Trabalho)"
                            className="text-sm"
                          />
                          <div className="flex gap-4">
                            <div className="flex-1">
                              <Label className="text-xs">Tipo de Resposta</Label>
                              <Select
                                value={pergunta.tipo}
                                onValueChange={(value) => {
                                  // Update tipo and opcoes in a single state update to avoid race condition
                                  const newPerguntas = [...formData.perguntas];
                                  newPerguntas[index] = { 
                                    ...newPerguntas[index], 
                                    tipo: value,
                                    opcoes: value === 'multipla_escolha' 
                                      ? (pergunta.opcoes && pergunta.opcoes.length > 0 ? pergunta.opcoes : ['', ''])
                                      : []
                                  };
                                  setFormData({ ...formData, perguntas: newPerguntas });
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="escala_1_4">Escala 1-4</SelectItem>
                                  <SelectItem value="escala_1_5">Escala 1-5</SelectItem>
                                  <SelectItem value="sim_nao">Sim/Não</SelectItem>
                                  <SelectItem value="texto_livre">Texto Livre</SelectItem>
                                  <SelectItem value="multipla_escolha">Múltipla Escolha</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex items-end">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={pergunta.obrigatoria}
                                  onChange={(e) => updateQuestion(index, 'obrigatoria', e.target.checked)}
                                  className="rounded"
                                />
                                Obrigatória
                              </label>
                            </div>
                          </div>
                          
                          {/* Opções para Múltipla Escolha */}
                          {pergunta.tipo === 'multipla_escolha' && (
                            <div className="mt-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                              <Label className="text-xs text-slate-600 dark:text-slate-400 mb-2 block">
                                Opções de Resposta
                              </Label>
                              <div className="space-y-2">
                                {(pergunta.opcoes || []).map((opcao, opcaoIndex) => (
                                  <div key={opcaoIndex} className="flex items-center gap-2">
                                    <span className="text-xs text-slate-500 w-5">{opcaoIndex + 1}.</span>
                                    <Input
                                      value={opcao}
                                      onChange={(e) => {
                                        const novasOpcoes = [...(pergunta.opcoes || [])];
                                        novasOpcoes[opcaoIndex] = e.target.value;
                                        updateQuestion(index, 'opcoes', novasOpcoes);
                                      }}
                                      placeholder={`Opção ${opcaoIndex + 1}`}
                                      className="flex-1 h-8 text-sm"
                                    />
                                    {(pergunta.opcoes || []).length > 2 && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          const novasOpcoes = (pergunta.opcoes || []).filter((_, i) => i !== opcaoIndex);
                                          updateQuestion(index, 'opcoes', novasOpcoes);
                                        }}
                                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                      >
                                        <X size={14} />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const novasOpcoes = [...(pergunta.opcoes || []), ''];
                                  updateQuestion(index, 'opcoes', novasOpcoes);
                                }}
                                className="mt-2 h-7 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                              >
                                <PlusCircle size={12} className="mr-1" />
                                Adicionar Opção
                              </Button>
                            </div>
                          )}
                        </div>
                        {formData.perguntas.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeQuestion(index)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <X size={18} />
                          </Button>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
                    Criar Avaliação
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Config Alert */}
      <AssessmentConfigAlert config={config} />

      {/* Stats Cards */}
      <AssessmentStatsCards 
        totalCount={assessments.length}
        activeCount={getActiveCount()}
        expiredCount={getExpiredCount()}
        totalSent={dbTotals.enviados || assessments.reduce((acc, a) => acc + (a.total_enviados || 0), 0)}
        totalResponses={dbTotals.respondidos || assessments.reduce((acc, a) => acc + (a.total_respondidos || 0), 0)}
      />

      {/* Assessments List */}
      <div className="space-y-4">
        {assessments.length === 0 ? (
          <AssessmentEmptyState />
        ) : (
          assessments.map((assessment) => (
            <AssessmentCard
              key={assessment.id}
              assessment={assessment}
              onSend={handleSendAssessment}
              onDownloadPDF={handleDownloadPDF}
              onExportResponses={handleExportResponses}
              onViewParticipation={handleViewParticipation}
              onViewCopsoqResults={fetchCopsoqResults}
              onViewResults={handleViewResults}
              onEdit={handleEditAssessment}
              onDelete={handleDeleteAssessment}
              loadingCopsoqResults={loadingCopsoqResults}
            />
          ))
        )}
      </div>

      {/* Results Dialog */}
      <Dialog open={resultsOpen} onOpenChange={setResultsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resultados: {selectedAssessment?.titulo || selectedAssessment?.title || 'Avaliação'}</DialogTitle>
          </DialogHeader>
          {results ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">{results?.total_respostas ?? results?.total_responses ?? 0}</p>
                  <p className="text-sm text-slate-500">Total de Respostas</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{results?.indice_geral ?? '-'}</p>
                  <p className="text-sm text-slate-500">Índice Geral (1-5)</p>
                </Card>
                <Card className="p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">
                    {Object.keys(results?.resultados_por_setor || {}).length}
                  </p>
                  <p className="text-sm text-slate-500">Setores</p>
                </Card>
              </div>

              {/* Results by Sector (Anonymous) */}
              <div>
                <h4 className="font-bold text-lg mb-4">Resultados por Setor (Anônimo)</h4>
                {results?.resultados_por_setor && Object.keys(results.resultados_por_setor).length > 0 ? (
                  Object.entries(results.resultados_por_setor).map(([setor, data]) => (
                    <Card key={setor} className="p-4 mb-4">
                      <h5 className="font-semibold text-slate-900 dark:text-white mb-3">
                        {setor || 'Setor não identificado'} ({data?.total_respostas ?? 0} respostas)
                      </h5>
                      <div className="space-y-2">
                        {(data?.perguntas || []).map((p, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                            <span className="text-sm text-slate-600 dark:text-slate-400 flex-1">
                              {p?.pergunta || p?.question_text || 'Pergunta não disponível'}
                            </span>
                            {(p?.tipo === 'escala_1_4' || p?.tipo === 'escala') && p?.media !== undefined && p?.media !== null && (
                              <span className={`font-bold ${
                                p.media <= 2 ? 'text-emerald-600' : 
                                p.media <= 3 ? 'text-yellow-600' : 
                                p.media <= 4 ? 'text-orange-600' : 'text-red-600'
                              }`}>
                                Média: {typeof p.media === 'number' ? p.media.toFixed(2) : p.media}
                              </span>
                            )}
                            {p?.tipo === 'sim_nao' && p?.distribuicao && (
                              <span className="text-sm">
                                Sim: {p.distribuicao?.percentual_sim ?? 0}%
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))
                ) : (
                  <Card className="p-4 text-center text-slate-500">
                    <p>Nenhum resultado por setor disponível ainda.</p>
                    <p className="text-sm mt-2">Os resultados aparecerão quando houver respostas.</p>
                  </Card>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => handleDownloadPDF(selectedAssessment?.id, 'results')}>
                  <Download size={16} className="mr-2" />
                  Baixar PDF dos Resultados
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p>Carregando resultados...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Participation Dialog */}
      <Dialog open={participationOpen} onOpenChange={setParticipationOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Participação: {selectedAssessment?.titulo}</DialogTitle>
          </DialogHeader>
          {participation && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 text-center">
                  <p className="text-2xl font-bold">{participation.total_enviados}</p>
                  <p className="text-xs text-slate-500">Enviados</p>
                </Card>
                <Card className="p-4 text-center bg-emerald-50 dark:bg-emerald-950">
                  <p className="text-2xl font-bold text-emerald-600">{participation.total_respondidos}</p>
                  <p className="text-xs text-slate-500">Responderam</p>
                </Card>
                <Card className="p-4 text-center bg-yellow-50 dark:bg-yellow-950">
                  <p className="text-2xl font-bold text-yellow-600">{participation.total_pendentes}</p>
                  <p className="text-xs text-slate-500">Pendentes</p>
                </Card>
                <Card className="p-4 text-center bg-blue-50 dark:bg-blue-950">
                  <p className="text-2xl font-bold text-blue-600">{participation.taxa_participacao}%</p>
                  <p className="text-xs text-slate-500">Taxa</p>
                </Card>
              </div>

              {/* By Sector */}
              <div>
                <h4 className="font-bold mb-3">Participação por Setor</h4>
                <div className="space-y-2">
                  {Object.entries(participation.por_setor || {}).map(([setor, data]) => (
                    <div key={setor} className="flex items-center gap-4">
                      <span className="w-32 text-sm font-medium">{setor}</span>
                      <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-4">
                        <div
                          className="bg-emerald-500 h-4 rounded-full transition-all"
                          style={{ width: `${data.percentual}%` }}
                        />
                      </div>
                      <span className="text-sm w-24 text-right">
                        {data.respondidos}/{data.total} ({data.percentual}%)
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tabs for Respondidos/Pendentes */}
              <Tabs defaultValue="respondidos">
                <TabsList>
                  <TabsTrigger value="respondidos">
                    <CheckCircle size={16} className="mr-1" />
                    Responderam ({participation.respondidos?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="pendentes">
                    <Clock size={16} className="mr-1" />
                    Pendentes ({participation.pendentes?.length || 0})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="respondidos" className="mt-4">
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {participation.respondidos?.map((emp, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                        <div>
                          <p className="font-medium">{emp.nome}</p>
                          <p className="text-sm text-slate-500">{emp.setor}</p>
                        </div>
                        <span className="text-xs text-emerald-600">
                          {emp.data_resposta ? new Date(emp.data_resposta).toLocaleDateString('pt-BR') : '-'}
                        </span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="pendentes" className="mt-4">
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {participation.pendentes?.map((emp, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                        <div>
                          <p className="font-medium">{emp.nome}</p>
                          <p className="text-sm text-slate-500">{emp.setor} - {emp.email}</p>
                        </div>
                        <span className="text-xs text-yellow-600">Aguardando resposta</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configurar Periodicidade NR-1</DialogTitle>
          </DialogHeader>
          {config && (
            <form onSubmit={handleUpdateConfig} className="space-y-4">
              <div>
                <Label>Frequência de Avaliações</Label>
                <Select
                  value={config.frequencia}
                  onValueChange={(value) => setConfig({ ...config, frequencia: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Dias de Antecedência para Aviso</Label>
                <Input
                  type="number"
                  value={config.dias_antecedencia_aviso}
                  onChange={(e) => setConfig({ ...config, dias_antecedencia_aviso: parseInt(e.target.value) })}
                  min={1}
                  max={90}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Um alerta será criado X dias antes da próxima avaliação obrigatória.
                </p>
              </div>
              <Card className="p-4 bg-blue-50 dark:bg-blue-950">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>NR-1:</strong> A avaliação psicossocial deve ser realizada periodicamente para 
                  identificar riscos relacionados à organização do trabalho e à saúde mental dos trabalhadores.
                </p>
              </Card>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setConfigOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
                  Salvar Configuração
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Send Assessment Modal */}
      <SendModal
        open={sendOpen}
        onOpenChange={setSendOpen}
        title="Enviar Avaliação"
        itemTitle={selectedAssessmentForSend?.titulo || selectedAssessmentForSend?.title}
        itemDescription={selectedAssessmentForSend?.descricao || selectedAssessmentForSend?.description}
        onSend={confirmSendAssessment}
        showMethodSelector={true}
        defaultMethod="whatsapp"
      />
      
      {/* Edit Assessment Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Avaliação</DialogTitle>
            <DialogDescription>
              Modifique as informações da avaliação
            </DialogDescription>
          </DialogHeader>
          
          {editData && (
            <div className="space-y-4">
              <div>
                <Label>Título</Label>
                <Input
                  value={editData.titulo}
                  onChange={(e) => setEditData({ ...editData, titulo: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={editData.descricao}
                  onChange={(e) => setEditData({ ...editData, descricao: e.target.value })}
                  rows={2}
                />
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">Perguntas</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addEditQuestion}>
                    <PlusCircle size={16} className="mr-1" />
                    Adicionar
                  </Button>
                </div>
                
                {editData.perguntas.map((pergunta, index) => (
                  <Card key={index} className="p-4 bg-slate-50">
                    <div className="flex items-start gap-3">
                      <span className="bg-emerald-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </span>
                      <div className="flex-1 space-y-2">
                        <Input
                          value={pergunta.texto}
                          onChange={(e) => updateEditQuestion(index, 'texto', e.target.value)}
                          placeholder="Texto da pergunta..."
                        />
                        <Input
                          value={pergunta.categoria || ''}
                          onChange={(e) => updateEditQuestion(index, 'categoria', e.target.value)}
                          placeholder="Categoria/Identificação"
                          className="text-sm"
                        />
                        <div className="flex gap-3">
                          <Select
                            value={pergunta.tipo_resposta}
                            onValueChange={(value) => {
                              // Update tipo_resposta and opcoes in a single state update to avoid race condition
                              const newPerguntas = [...editData.perguntas];
                              newPerguntas[index] = { 
                                ...newPerguntas[index], 
                                tipo_resposta: value,
                                opcoes: value === 'multipla_escolha' 
                                  ? (pergunta.opcoes && pergunta.opcoes.length > 0 ? pergunta.opcoes : ['', ''])
                                  : []
                              };
                              setEditData({ ...editData, perguntas: newPerguntas });
                            }}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="escala_1_4">Escala 1-4</SelectItem>
                              <SelectItem value="escala_1_5">Escala 1-5</SelectItem>
                              <SelectItem value="sim_nao">Sim/Não</SelectItem>
                              <SelectItem value="texto_livre">Texto Livre</SelectItem>
                              <SelectItem value="multipla_escolha">Múltipla Escolha</SelectItem>
                            </SelectContent>
                          </Select>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={pergunta.obrigatoria}
                              onChange={(e) => updateEditQuestion(index, 'obrigatoria', e.target.checked)}
                              className="rounded"
                            />
                            Obrigatória
                          </label>
                        </div>
                        
                        {/* Opções para Múltipla Escolha (Edit Modal) */}
                        {pergunta.tipo_resposta === 'multipla_escolha' && (
                          <div className="mt-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                            <Label className="text-xs text-slate-600 dark:text-slate-400 mb-2 block">
                              Opções de Resposta
                            </Label>
                            <div className="space-y-2">
                              {(pergunta.opcoes || []).map((opcao, opcaoIndex) => (
                                <div key={opcaoIndex} className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500 w-5">{opcaoIndex + 1}.</span>
                                  <Input
                                    value={opcao}
                                    onChange={(e) => {
                                      const novasOpcoes = [...(pergunta.opcoes || [])];
                                      novasOpcoes[opcaoIndex] = e.target.value;
                                      updateEditQuestion(index, 'opcoes', novasOpcoes);
                                    }}
                                    placeholder={`Opção ${opcaoIndex + 1}`}
                                    className="flex-1 h-8 text-sm"
                                  />
                                  {(pergunta.opcoes || []).length > 2 && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const novasOpcoes = (pergunta.opcoes || []).filter((_, i) => i !== opcaoIndex);
                                        updateEditQuestion(index, 'opcoes', novasOpcoes);
                                      }}
                                      className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                    >
                                      <X size={14} />
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const novasOpcoes = [...(pergunta.opcoes || []), ''];
                                updateEditQuestion(index, 'opcoes', novasOpcoes);
                              }}
                              className="mt-2 h-7 text-xs text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                            >
                              <PlusCircle size={12} className="mr-1" />
                              Adicionar Opção
                            </Button>
                          </div>
                        )}
                      </div>
                      {editData.perguntas.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEditQuestion(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X size={18} />
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} className="bg-emerald-500 hover:bg-emerald-600">
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* COPSOQ II Results Dialog */}
      <Dialog open={copsoqResultsOpen} onOpenChange={setCopsoqResultsOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-6 w-6 text-purple-600" />
              Resultados COPSOQ II
              <Badge className="bg-purple-500 ml-2">Versão Média Portugal 2013</Badge>
            </DialogTitle>
          </DialogHeader>
          
          {copsoqResults && (
            <div className="space-y-6">
              {/* Assessment Info */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <h3 className="font-semibold text-lg">{copsoqResults.assessment?.titulo}</h3>
                <p className="text-sm text-slate-500 mt-1">{copsoqResults.assessment?.descricao}</p>
              </div>

              {/* Summary Cards */}
              {copsoqResults.resultados && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="p-4 text-center">
                      <p className="text-3xl font-bold text-blue-600">
                        {copsoqResults.resultados.total_participantes}
                      </p>
                      <p className="text-sm text-slate-500">Participantes</p>
                    </Card>
                    <Card className="p-4 text-center">
                      <p className="text-3xl font-bold text-purple-600">
                        {copsoqResults.resultados.resumo?.media_geral}
                      </p>
                      <p className="text-sm text-slate-500">Média Geral (1-5)</p>
                    </Card>
                    <Card className="p-4 text-center bg-green-50 dark:bg-green-900/20">
                      <p className="text-3xl font-bold text-green-600">
                        {copsoqResults.resultados.resumo?.distribuicao_semaforo?.verde || 0}
                      </p>
                      <p className="text-sm text-slate-500">Favoráveis</p>
                    </Card>
                    <Card className="p-4 text-center bg-red-50 dark:bg-red-900/20">
                      <p className="text-3xl font-bold text-red-600">
                        {copsoqResults.resultados.resumo?.distribuicao_semaforo?.vermelho || 0}
                      </p>
                      <p className="text-sm text-slate-500">Risco</p>
                    </Card>
                  </div>

                  {/* Overall Status */}
                  <Card className={`p-4 border-l-4 ${
                    copsoqResults.resultados.resumo?.status === 'favoravel' ? 'border-l-green-500 bg-green-50 dark:bg-green-900/20' :
                    copsoqResults.resultados.resumo?.status === 'moderado' ? 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20' :
                    copsoqResults.resultados.resumo?.status === 'atencao' ? 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/20' :
                    'border-l-red-500 bg-red-50 dark:bg-red-900/20'
                  }`}>
                    <h4 className="font-semibold">Situação Geral</h4>
                    <p className="text-lg mt-1">{copsoqResults.resultados.resumo?.interpretacao}</p>
                  </Card>

                  {/* Dimensions Results - Bar Chart Style */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Resultados por Dimensão
                    </h4>
                    
                    <div className="space-y-3">
                      {Object.values(copsoqResults.resultados.dimensoes || {}).map((dim) => (
                        <div key={dim.id} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h5 className="font-medium" style={{ color: dim.cor }}>{dim.nome}</h5>
                              <p className="text-xs text-slate-500">{dim.descricao}</p>
                            </div>
                            <div className="text-right">
                              <Badge 
                                style={{ backgroundColor: dim.cor_semaforo }}
                                className="text-white"
                              >
                                {dim.interpretacao}
                              </Badge>
                              <p className="text-sm text-slate-600 mt-1">
                                Média: <strong>{dim.media}</strong> ({dim.total_respostas} respostas)
                              </p>
                            </div>
                          </div>
                          {/* Progress bar visualization */}
                          <div className="relative h-6 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className="absolute h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${(dim.media / 5) * 100}%`,
                                backgroundColor: dim.cor_semaforo
                              }}
                            />
                            {/* Reference lines for thresholds */}
                            <div className="absolute h-full w-px bg-red-500 opacity-50" style={{ left: '46.6%' }} title="2.33" />
                            <div className="absolute h-full w-px bg-green-500 opacity-50" style={{ left: '73.2%' }} title="3.66" />
                          </div>
                          <div className="flex justify-between text-xs text-slate-400 mt-1">
                            <span>1 (Risco)</span>
                            <span>2.33</span>
                            <span>3.66</span>
                            <span>5 (Favorável)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommendations */}
                  {copsoqResults.resultados.recomendacoes?.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-lg flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Recomendações de Intervenção
                      </h4>
                      <div className="space-y-2">
                        {copsoqResults.resultados.recomendacoes.map((rec, index) => (
                          <Card 
                            key={index} 
                            className="p-3 border-l-4"
                            style={{ borderLeftColor: rec.cor }}
                          >
                            <div className="flex items-start gap-2">
                              <Badge 
                                style={{ backgroundColor: rec.cor }} 
                                className="text-white text-xs shrink-0"
                              >
                                {rec.prioridade === 'alta' ? 'Prioritário' : 'Monitorar'}
                              </Badge>
                              <div>
                                <p className="font-medium text-sm">{rec.dimensao}</p>
                                <p className="text-sm text-slate-600 dark:text-slate-400">{rec.texto}</p>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {!copsoqResults.resultados && (
                <div className="text-center py-8 text-slate-500">
                  <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{copsoqResults.mensagem || 'Nenhuma resposta recebida ainda'}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2 flex-wrap">
              {/* Sync to PGR Button */}
              <Button
                variant="outline"
                onClick={() => handleSyncToPgr(selectedCopsoqAssessmentId)}
                disabled={syncingToPgr || !copsoqResults?.resultados}
                className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                data-testid="sync-pgr-btn"
              >
                {syncingToPgr ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <RefreshCw size={16} className="mr-2" />
                )}
                {syncingToPgr ? 'Sincronizando...' : 'Sincronizar com PGR'}
              </Button>
              
              {/* Individual Reports Button */}
              <Button
                variant="outline"
                onClick={() => {
                  setCopsoqResultsOpen(false);
                  fetchParticipants(selectedCopsoqAssessmentId);
                }}
                disabled={loadingParticipants || !copsoqResults?.resultados}
                className="border-purple-500 text-purple-600 hover:bg-purple-50"
                data-testid="individual-reports-btn"
              >
                {loadingParticipants ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <FileUser size={16} className="mr-2" />
                )}
                Laudos Individuais
              </Button>
            </div>
            
            <Button variant="outline" onClick={() => setCopsoqResultsOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Individual Reports Dialog - Participants List */}
      <Dialog open={participantsOpen} onOpenChange={setParticipantsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUser className="h-6 w-6 text-purple-600" />
              Laudos Individuais - COPSOQ II
            </DialogTitle>
            <DialogDescription>
              Selecione um funcionário para gerar o laudo individual. Este documento é <strong>confidencial</strong> e protegido pela LGPD.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* LGPD Warning */}
            <Card className="p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Aviso LGPD:</strong> Os laudos individuais contêm dados sensíveis de saúde. 
                  O acesso é restrito a administradores autorizados. Todas as gerações são registradas em log de auditoria.
                </div>
              </div>
            </Card>

            {/* Participants List */}
            {participants.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum funcionário respondeu esta avaliação ainda.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-slate-500 mb-3">
                  {participants.length} funcionário(s) responderam esta avaliação
                </p>
                
                {participants.map((emp) => (
                  <Card 
                    key={emp.id} 
                    className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    data-testid={`participant-${emp.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded-full">
                        <Users className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{emp.nome}</p>
                        <p className="text-sm text-slate-500">
                          {emp.cargo || 'Cargo não informado'} • {emp.setor || 'Setor não informado'}
                        </p>
                        {emp.submitted_at && (
                          <p className="text-xs text-slate-400">
                            Respondido em: {new Date(emp.submitted_at).toLocaleDateString('pt-BR')}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      size="sm"
                      onClick={() => handleGenerateIndividualReport(selectedCopsoqAssessmentId, emp.id, emp.nome)}
                      disabled={generatingReport === emp.id}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid={`generate-report-${emp.id}`}
                    >
                      {generatingReport === emp.id ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Gerando...
                        </>
                      ) : (
                        <>
                          <Download size={16} className="mr-2" />
                          Gerar Laudo PDF
                        </>
                      )}
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setParticipantsOpen(false);
                setCopsoqResultsOpen(true);
              }}
            >
              Voltar aos Resultados
            </Button>
            <Button variant="outline" onClick={() => setParticipantsOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assessments;
